import type { IconStyle } from "@/types/accountApiDef"
import { defineStore } from "pinia"
import { ref } from 'vue'
import { useAccountStore } from "@/stores/account"

export type AccountIcon = {
  icon_url: string;
  icon_style: IconStyle;
}

export type AccountProfile = {
  account_id: string;
  account_name: string;
  company_name?: string;
  department_name?: string;
} & AccountIcon

export const useAccountProfilesStore = defineStore('accountProfiles', () => {
  // アカウントプロフィール
  const __accountProfileMap = ref<{[accountId: string]: AccountProfile}>({})

  const retrieveAccountInfo = async (accountId: string) => {
    // 登録されていない場合、アカウント情報取得APIから取得する
    const accountStore = useAccountStore()
    const apiArgs = {
      account_id: accountId,
      __apiOptions: { noDisplayLoading: true },
    }
    const accountInfo = await accountStore.getAccountInfo(apiArgs)
    if (accountInfo) {
      // アカウント名マップに追加
      updateAccountProfileMap([{
        account_id: accountId,
        account_name: accountInfo.account_name,
        company_name: accountInfo.company_name,
        department_name: accountInfo.department_name,
        icon_style: accountInfo.icon_style,
        icon_url: accountInfo.icon_url
      }])
    }

    return accountInfo
  }

  // アカウント名を取得する
  const getAccountName = async (accountId: string) => {
    const accountProfile = __accountProfileMap.value[accountId]

    // 登録されている場合
    if(accountProfile) return accountProfile.account_name

    // 登録されていない場合、アカウント情報取得APIから取得する
    const accountInfo = await retrieveAccountInfo(accountId)
    if (!accountInfo)  return '';

    return accountInfo.account_name
  }

  // アカウントアイコンを取得する
  const getAccountIcon = async (accountId: string): Promise<AccountIcon | undefined> => {
    const accountProfile = __accountProfileMap.value[accountId]

    // 登録されている場合
    if(accountProfile) return {
      icon_url: accountProfile.icon_url,
      icon_style: accountProfile.icon_style,
    }

    // 登録されていない場合、アカウント情報取得APIから取得する
    const accountInfo = await retrieveAccountInfo(accountId)
    if (!accountInfo)  return;

    return {
      icon_url: accountInfo.icon_url,
      icon_style: accountInfo.icon_style,
    }
  }

  // アカウントプロフィールを返す（キャッシュ済みのもののみ）
  const getAccountProfile = (accountId: string): AccountProfile | undefined => {
    return __accountProfileMap.value[accountId]
  }

  // アカウントIDリストに対応するプロフィールの配列を返す（キャッシュ済みのもののみ）
  const getAccountProfileList = (accountIds: string[]): AccountProfile[] => {
    return accountIds
      .map(id => __accountProfileMap.value[id])
      .filter((p): p is AccountProfile => !!p)
  }

  // アカウント名マップの更新（既存プロフィールのフィールドを保持しつつマージ）
  const updateAccountProfileMap = (accountList: Array<AccountProfile>) =>{
    const newMap = { ...__accountProfileMap.value }
    for (const profile of accountList) {
      const existing = newMap[profile.account_id]
      newMap[profile.account_id] = existing ? { ...existing, ...profile } : profile
    }
    __accountProfileMap.value = newMap
  }

  return {
    __accountProfileMap,
    retrieveAccountInfo,
    getAccountName,
    getAccountIcon,
    getAccountProfile,
    getAccountProfileList,
    updateAccountProfileMap,
  }
})
